import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';

function fmt(t) {
  const [h,m] = t.split(':').map(Number);
  return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;
}

const WEEKDAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const MONTHS   = ['January','February','March','April','May','June',
                   'July','August','September','October','November','December'];

const STATUS_COLOR = {
  confirmed: '#10b981',
  pending:   '#f59e0b',
  cancelled: '#94a3b8',
  no_show:   '#7c3aed',
};

function todayGMT2() {
  return new Date(new Date().getTime() + 2 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function getKey(y, m) { return `${y}-${String(m+1).padStart(2,'0')}`; }

export default function CalendarView() {
  const now = new Date(new Date().getTime() + 2 * 60 * 60 * 1000);
  const [year, setYear]   = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth());
  const [data, setData]   = useState({});
  const [loading, setLoading] = useState(false);
  const [selDay, setSelDay]   = useState(null);

  // Persistent cache: key "YYYY-MM" -> { date: [reservations] }
  const cacheRef  = useRef({});
  // Track which month is currently displayed to avoid stale updates
  const activeKey = useRef(null);

  // Fetch a single month and store in cache. No-ops if already cached.
  const fetchOne = useCallback(async (y, m) => {
    const key = getKey(y, m);
    if (cacheRef.current[key] !== undefined) return;
    const from = key + '-01';
    const lastDay = new Date(Date.UTC(y, m+1, 0)).getUTCDate();
    const to = `${key}-${String(lastDay).padStart(2,'0')}`;
    const rows = await api.getReservations({ date_from: from, date_to: to });
    const map = {};
    for (const r of rows) {
      if (!map[r.date]) map[r.date] = [];
      map[r.date].push(r);
    }
    cacheRef.current[key] = map;
  }, []);

  useEffect(() => {
    const key = getKey(year, month);
    activeKey.current = key;

    // Show cached data instantly if available
    if (cacheRef.current[key] !== undefined) {
      setData(cacheRef.current[key]);
      setLoading(false);
    } else {
      setLoading(true);
    }

    // Fetch current month (no-op if cached, updates display when done)
    fetchOne(year, month)
      .then(() => {
        if (activeKey.current === key) {
          setData(cacheRef.current[key] || {});
          setLoading(false);
        }
      })
      .catch(() => { if (activeKey.current === key) setLoading(false); });

    // Silently preload the adjacent months so flipping is instant next time
    const [py, pm] = month === 0 ? [year-1, 11] : [year, month-1];
    const [ny, nm] = month === 11 ? [year+1, 0] : [year, month+1];
    fetchOne(py, pm).catch(() => {});
    fetchOne(ny, nm).catch(() => {});
  }, [year, month, fetchOne]);

  // On a global refresh, invalidate the current month's cache and reload
  useEffect(() => {
    const handler = () => {
      const key = getKey(year, month);
      delete cacheRef.current[key];
      activeKey.current = key;
      setLoading(true);
      fetchOne(year, month)
        .then(() => { if (activeKey.current === key) { setData(cacheRef.current[key] || {}); setLoading(false); }})
        .catch(() => { if (activeKey.current === key) setLoading(false); });
    };
    window.addEventListener('seva:refresh', handler);
    return () => window.removeEventListener('seva:refresh', handler);
  }, [year, month, fetchOne]);

  const prevMonth = () => {
    if (month === 0) { setYear(y => y-1); setMonth(11); } else setMonth(m => m-1);
    setSelDay(null);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y+1); setMonth(0); } else setMonth(m => m+1);
    setSelDay(null);
  };

  // Build grid cells (Mon-first)
  const firstDow    = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month+1, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const TODAY = todayGMT2();
  const selectedRows = selDay
    ? (data[selDay] || []).slice().sort((a,b) => a.time_slot.localeCompare(b.time_slot))
    : [];

  return (
    <div>
      <h1 style={s.h1}>Calendar</h1>
      <p style={s.sub}>Booking overview by day. Click any date to see details.</p>

      <div style={s.calWrap}>
        {/* Month navigation */}
        <div style={s.calHead}>
          <button style={s.navBtn} onClick={prevMonth}>‹</button>
          <span style={s.monthLabel}>
            {MONTHS[month]} {year}
            {loading && <span style={s.spinner}> ·</span>}
          </span>
          <button style={s.navBtn} onClick={nextMonth}>›</button>
        </div>

        {/* Weekday headers */}
        <div style={s.grid7}>
          {WEEKDAYS.map(d => <div key={d} style={s.weekLabel}>{d}</div>)}
        </div>

        {/* Day cells */}
        <div style={s.grid7}>
          {cells.map((day, i) => {
            if (!day) return <div key={`e${i}`} style={s.emptyCell} />;
            const dateStr   = `${getKey(year, month)}-${String(day).padStart(2,'0')}`;
            const allResos  = data[dateStr] || [];
            const active    = allResos.filter(r => !['cancelled','no_show'].includes(r.status));
            const isToday   = dateStr === TODAY;
            const isSel     = dateStr === selDay;
            const isPastDay = dateStr < TODAY;
            const countColor = active.length >= 8 ? '#ef4444' : active.length >= 4 ? '#f59e0b' : '#4f46e5';

            return (
              <div
                key={day}
                style={{
                  ...s.cell,
                  ...(isToday   ? s.cellToday : {}),
                  ...(isSel     ? s.cellSel   : {}),
                  ...(isPastDay && !isToday ? { opacity: 0.55 } : {}),
                }}
                onClick={() => setSelDay(isSel ? null : dateStr)}
              >
                <span style={{ ...s.dayNum, ...(isToday ? s.dayNumToday : {}) }}>{day}</span>
                {active.length > 0 && (
                  <span style={{ ...s.countBadge, background: countColor }}>
                    {active.length}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={s.legend}>
        <span style={{ ...s.legendDot, background:'#4f46e5' }} /> 1–3 bookings
        <span style={{ ...s.legendDot, background:'#f59e0b', marginLeft:16 }} /> 4–7 bookings
        <span style={{ ...s.legendDot, background:'#ef4444', marginLeft:16 }} /> 8+ bookings
      </div>

      {/* Selected day detail */}
      {selDay && (
        <div style={s.dayPanel}>
          <div style={s.dayPanelHead}>
            <div>
              <h3 style={s.dayPanelTitle}>
                {new Date(selDay + 'T12:00:00Z').toLocaleDateString('en-US', {
                  weekday:'long', year:'numeric', month:'long', day:'numeric'
                })}
              </h3>
              <div style={s.dayPanelSub}>
                {selectedRows.filter(r=>!['cancelled','no_show'].includes(r.status)).length} active ·{' '}
                {selectedRows.length} total
              </div>
            </div>
            <button style={s.closeBtn} onClick={()=>setSelDay(null)}>×</button>
          </div>

          {selectedRows.length === 0 ? (
            <div style={s.noBookings}>No reservations on this day.</div>
          ) : (
            <div style={s.rList}>
              {selectedRows.map(r => (
                <div
                  key={r.id}
                  style={{
                    ...s.rCard,
                    opacity: ['cancelled','no_show'].includes(r.status) ? 0.5 : 1,
                  }}
                >
                  <div style={s.rTime}>{fmt(r.time_slot)}</div>
                  <div style={s.rInfo}>
                    <span style={s.rName}>{r.customer_name}</span>
                    <span style={s.rMeta}>{r.party_size} guest{r.party_size!==1?'s':''} · {r.tables_display||r.table_label}</span>
                  </div>
                  <div style={s.rRight}>
                    {r.source === 'ai' && <span style={s.aiTag}>AI</span>}
                    <span style={{
                      ...s.rBadge,
                      background: (STATUS_COLOR[r.status]||'#94a3b8') + '22',
                      color: STATUS_COLOR[r.status]||'#94a3b8',
                    }}>
                      {r.status.replace('_',' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  h1:      { fontSize:28, fontWeight:800, color:'var(--text-primary)', margin:0, letterSpacing:'-0.5px' },
  sub:     { fontSize:14, color:'var(--text-muted)', marginTop:4, marginBottom:24 },
  spinner: { color:'#94a3b8', fontSize:14 },

  calWrap: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:18, overflow:'hidden',
             boxShadow:'0 1px 4px rgba(15,23,42,0.06)', marginBottom:14, maxWidth:660 },
  calHead: { display:'flex', alignItems:'center', justifyContent:'space-between',
             padding:'16px 20px', borderBottom:'1px solid #f1f5f9' },
  navBtn:  { background:'none', border:'1px solid #e2e8f0', borderRadius:8, width:36, height:36,
             fontSize:22, lineHeight:1, cursor:'pointer', color:'var(--text-secondary)',
             display:'flex', alignItems:'center', justifyContent:'center',
             transition:'background 0.1s' },
  monthLabel:{ fontSize:16, fontWeight:700, color:'var(--text-primary)' },
  grid7:   { display:'grid', gridTemplateColumns:'repeat(7,1fr)' },
  weekLabel:{ padding:'10px 0 6px', fontSize:11, fontWeight:600, color:'var(--text-muted)',
              textAlign:'center', textTransform:'uppercase', letterSpacing:'0.07em' },
  emptyCell:{ minHeight:72 },
  cell:    { minHeight:72, display:'flex', flexDirection:'column', alignItems:'center',
             justifyContent:'center', gap:5, cursor:'pointer', borderTop:'1px solid #f1f5f9',
             transition:'background 0.1s', padding:'6px 4px', userSelect:'none' },
  cellToday:{ background:'#f0f9ff' },
  cellSel:  { background:'#eef2ff', outline:'2px solid #4f46e5', outlineOffset:'-2px',
              zIndex:1, position:'relative' },
  dayNum:      { fontSize:14, fontWeight:500, color:'var(--text-secondary)', lineHeight:1 },
  dayNumToday: { fontWeight:800, color:'#4f46e5' },
  countBadge:  { fontSize:11, fontWeight:700, color:'#fff', borderRadius:10, padding:'2px 8px', lineHeight:'16px' },

  legend:   { display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-muted)', marginBottom:24 },
  legendDot:{ display:'inline-block', width:10, height:10, borderRadius:'50%' },

  dayPanel: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:18, padding:'22px 24px',
              boxShadow:'0 1px 4px rgba(15,23,42,0.06)', maxWidth:660 },
  dayPanelHead: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18, gap:12 },
  dayPanelTitle:{ fontSize:17, fontWeight:700, color:'var(--text-primary)', margin:'0 0 4px' },
  dayPanelSub:  { fontSize:13, color:'var(--text-muted)' },
  closeBtn: { background:'var(--section-bg)', border:'1px solid var(--section-border)', borderRadius:8,
              width:32, height:32, fontSize:20, cursor:'pointer', color:'var(--text-secondary)', flexShrink:0 },
  noBookings:{ color:'var(--text-muted)', fontSize:14, padding:'8px 0' },

  rList:   { display:'flex', flexDirection:'column', gap:8 },
  rCard:   { display:'flex', alignItems:'center', gap:14, padding:'11px 14px',
             background:'#f8fafc', borderRadius:10, border:'1px solid #f1f5f9' },
  rTime:   { fontSize:14, fontWeight:700, color:'var(--text-primary)', minWidth:74, flexShrink:0 },
  rInfo:   { flex:1, display:'flex', flexDirection:'column', gap:2 },
  rName:   { fontSize:14, fontWeight:600, color:'var(--text-primary)' },
  rMeta:   { fontSize:12, color:'var(--text-muted)' },
  rRight:  { display:'flex', alignItems:'center', gap:6, flexShrink:0 },
  aiTag:   { fontSize:11, color:'#4f46e5', fontWeight:700 },
  rBadge:  { fontSize:11, fontWeight:600, borderRadius:20, padding:'2px 9px' },
};
